import apiClient from './apiClient';

export type AudioRecordStatus = 'active' | 'promoted' | 'purged' | 'excluded';
export type AudioDataQuality = 'production' | 'evaluation' | 'suspect';
export type AudioFeedbackKind = 'correction' | 'confidence' | 'flag';

export interface ResolvedConsent {
  orgConsent: boolean;
  doctorConsent: boolean | null;
  effective: boolean;
  retentionDays: number;
  policyVersion: number;
}

export interface AudioRecord {
  id: string;
  tenantId: string;
  recordingSessionId: string;
  doctorId: string;
  bucket: string;
  objectKey: string;
  byteSize: number | null;
  durationMs: number | null;
  format: string;
  dekVersion: number;
  status: AudioRecordStatus;
  dataQuality: AudioDataQuality;
  capturedAt: string;
  expiresAt: string;
  promotedAt: string | null;
  excludedAt: string | null;
  purgedAt: string | null;
  createdAt: string;
}

export interface AudioRecordStats {
  active: number;
  promoted: number;
  purged: number;
  excluded: number;
  total: number;
}

export interface AudioFeedback {
  id: string;
  audioRecordId: string;
  kind: AudioFeedbackKind;
  tsStartMs: number | null;
  tsEndMs: number | null;
  payload: Record<string, unknown>;
  createdAt: string;
  createdById?: string;
}

export interface SetOrgConsentPayload {
  enabled: boolean;
  retentionDays?: number;
  reason?: string;
}

export interface SetDoctorOptOutPayload {
  optOut: boolean;
  reason?: string;
}

export interface SubmitFeedbackPayload {
  audioRecordId: string;
  kind: AudioFeedbackKind;
  tsStartMs?: number;
  tsEndMs?: number;
  payload: Record<string, unknown>;
}

export interface SaveTranscriptCorrectionPayload {
  audioRecordId: string;
  turnIndex: number;
  textOriginal: string;
  textCorrected: string;
  speakerOriginal?: string;
  speakerCorrected?: string;
  tsStartMs?: number;
  tsEndMs?: number;
  category?: string;
  notes?: string;
}

export interface TranscriptCorrection {
  id: string;
  audioRecordId: string;
  correctedBy: string;
  turnIndex: number;
  tsStartMs: number | null;
  tsEndMs: number | null;
  speakerOriginal: string | null;
  speakerCorrected: string | null;
  textOriginal: string;
  textCorrected: string;
  textDiff: string | null;
  version: number;
  category: string | null;
  notes: string | null;
  createdAt: string;
  updatedAt: string;
}

const BASE = '/audio-retention';

/**
 * Backend wraps every response in `{success, data, ...}` via the global
 * ResponseInterceptor. Earlier audioRetentionApi was treating
 * `axios.response.data` as the payload directly — it's actually the
 * envelope, so `myConsent().orgConsent` was always undefined and the
 * toggle UI never reflected the saved state. Unwrap defensively so a
 * future interceptor change (or a route that bypasses the wrap) still
 * works.
 */
function unwrap<T>(body: unknown): T {
  if (
    body !== null &&
    typeof body === 'object' &&
    'data' in (body as Record<string, unknown>) &&
    'success' in (body as Record<string, unknown>)
  ) {
    return (body as { data: T }).data;
  }
  return body as T;
}

export const audioRetentionApi = {
  // Org consent (clinic owner only)
  setOrgConsent: async (payload: SetOrgConsentPayload): Promise<void> => {
    await apiClient.post(`${BASE}/consent/org`, payload);
  },

  // Doctor self
  myConsent: async (): Promise<ResolvedConsent> => {
    const res = await apiClient.get(`${BASE}/consent/me`);
    return unwrap<ResolvedConsent>(res.data);
  },

  setMyOptOut: async (payload: SetDoctorOptOutPayload): Promise<void> => {
    await apiClient.post(`${BASE}/consent/me/opt-out`, payload);
  },

  // Records (clinic owner)
  listRecords: async (
    params: {
      status?: AudioRecordStatus;
      doctorId?: string;
      limit?: number;
    } = {},
  ): Promise<AudioRecord[]> => {
    const res = await apiClient.get(`${BASE}/records`, { params });
    return unwrap<AudioRecord[]>(res.data);
  },

  stats: async (): Promise<AudioRecordStats> => {
    const res = await apiClient.get(`${BASE}/records/stats`);
    return unwrap<AudioRecordStats>(res.data);
  },

  promote: async (id: string): Promise<AudioRecord> => {
    const res = await apiClient.post(`${BASE}/records/${id}/promote`);
    return unwrap<AudioRecord>(res.data);
  },

  exclude: async (id: string): Promise<AudioRecord> => {
    const res = await apiClient.post(`${BASE}/records/${id}/exclude`);
    return unwrap<AudioRecord>(res.data);
  },

  /**
   * Resolve the audio record for a given recording session — used by the
   * encounter UI to decide whether to show feedback affordances. Returns
   * null when retention was off for the session.
   */
  getBySession: async (
    recordingSessionId: string,
  ): Promise<AudioRecord | null> => {
    const res = await apiClient.get(
      `${BASE}/records/by-session/${recordingSessionId}`,
    );
    return unwrap<AudioRecord | null>(res.data);
  },

  // Feedback
  submitFeedback: async (
    payload: SubmitFeedbackPayload,
  ): Promise<AudioFeedback> => {
    const res = await apiClient.post(`${BASE}/feedback`, payload);
    return unwrap<AudioFeedback>(res.data);
  },

  listFeedback: async (audioRecordId: string): Promise<AudioFeedback[]> => {
    const res = await apiClient.get(`${BASE}/feedback`, {
      params: { audio_record_id: audioRecordId },
    });
    return unwrap<AudioFeedback[]>(res.data);
  },

  // Per-turn transcript corrections (first-class training rows)
  saveTranscriptCorrection: async (
    payload: SaveTranscriptCorrectionPayload,
  ): Promise<TranscriptCorrection> => {
    const res = await apiClient.post(
      `${BASE}/transcript-corrections`,
      payload,
    );
    return unwrap<TranscriptCorrection>(res.data);
  },

  listTranscriptCorrections: async (
    audioRecordId: string,
  ): Promise<TranscriptCorrection[]> => {
    const res = await apiClient.get(`${BASE}/transcript-corrections`, {
      params: { audio_record_id: audioRecordId },
    });
    return unwrap<TranscriptCorrection[]>(res.data);
  },

  // Bulk withdrawal
  withdrawAll: async (): Promise<{
    excluded: number;
    objectDeleteFailures: number;
  }> => {
    const res = await apiClient.post(`${BASE}/withdraw-all`);
    return unwrap<{ excluded: number; objectDeleteFailures: number }>(
      res.data,
    );
  },
};
